import React from 'react';

import MarkdownContent from '../common/MarkdownContent';
import MaskedValue from '../common/MaskedValue';
import UserValue from '../users/UserValue';
import { elementLabel, formatValue } from '../items/itemValueDisplay';
import { isMaskedElement } from '../../utils/maskedElements';
import { isHeading, visibleColumns } from '../../utils/repeatingGroupEntries';
import { cellBox, entriesOnLines, groupName, lineTopsOf } from './pageGroupLines';
import { sampleEntries } from './pageFormSample';

/**
 * A repeating group on a form's ruled lines (KPR-1863, KPE-041): the cells of one
 * `group` block, drawn by PageLayoutRender, so the editor and the printer read the
 * same thing. The geometry is pageGroupLines.js.
 *
 * A cell is a block's box for one value. It carries `data-fit-name`, and
 * `data-autoscale` when the block asks for it, so pageBlockFit measures, shrinks and
 * tells it with the code a block uses: there is no second measure. A line with no
 * entry draws nothing, and neither does a column with no value: the form shows through.
 *
 * WHO SEES WHAT. A cell is drawn only for a column in `visibleColumns`: a column
 * hidden from this person is absent from their card, or stamped hidden on an editor's,
 * and is neither drawn nor named, whatever an entry holds. A column the card has lost
 * reads the same way to a reader, which is why the print route says nothing about one
 * (the designer does, PageGroupInspector). `group` is null when the whole group is
 * unreadable, and then the block is blank.
 *
 * `sample` is the designer's: every line holds the columns' sample values, so the
 * person sees where the text will sit, and nothing counts as "not shown".
 */

const inUnits = (box, unit) => ({
	left: `${box.left}${unit}`,
	top: `${box.top}${unit}`,
	width: `${box.width}${unit}`,
	height: `${box.height}${unit}`,
});

const isEmpty = value => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

/** What a value prints as, or null for nothing to print: never an id, never an object. */
function cellValue(column, value, { resetKey, resolveLookupValue, unitSystem }) {
	if (isEmpty(value)) return null;
	if (isMaskedElement(column)) return <MaskedValue element={column} value={value} prefs={{ unitSystem }} resetKey={resetKey} empty="" />;
	// People, not ids: what the read view draws. formatValue gives the stored id.
	if (column.dataType === 'user') return <UserValue element={column} value={value} size="xs" />;
	if (column.dataType === 'rich-text') return <MarkdownContent markdown={String(value)} />;
	const text = formatValue(column, value, { unitSystem, resolveLookupValue });
	return text === '' || text === null || text === undefined || typeof text === 'object' ? null : text;
}

function Cell({ box, unit, name, cell, scaled, className, textStyle, children }) {
	return (
		<div
			className={`page-cell${className}`}
			style={inUnits(box, unit)}
			data-fit-name={name}
			data-fit-cell={cell}
			{...(scaled ? { 'data-autoscale': '' } : {})}
		>
			<div className="page-block__value" style={textStyle}>{children}</div>
		</div>
	);
}

export default function PageGroupBlock({ block, group, record, page, textStyle, onForm, sample = false, resolveLookupValue, unitSystem }) {
	if (!group) return null;
	const readable = new Map(visibleColumns(group).map(column => [column.name, column]));
	const placed = (block.columns || []).filter(column => readable.has(column.name));
	const entries = sample
		? sampleEntries(group, block.lines)
		: entriesOnLines(record?.elements?.[block.element], block.lines).shown;
	const tops = lineTopsOf(block);
	const named = groupName(group);
	const scaled = onForm && block.style?.autoScale === true;
	const where = line => (sample ? named : `${named}, line ${line + 1}`);

	return entries.flatMap((entry, line) => {
		if (isHeading(entry)) {
			return (
				<Cell
					key={`h${line}`}
					box={cellBox(block, line, { x: 0, w: block.w }, tops)}
					unit={page.unit}
					name={where(line)}
					cell={`h${line}`}
					scaled={scaled}
					className=" page-cell--heading"
					textStyle={textStyle}
				>
					{entry.section}
				</Cell>
			);
		}
		return placed.map((place) => {
			const column = readable.get(place.name);
			const value = cellValue(column, entry.elements?.[column.name], { resetKey: `${record?._id}:${entry.id}`, resolveLookupValue, unitSystem });
			if (value === null) return null;
			// The designer's lines all hold one sample: one row in its list, not a hundred.
			return (
				<Cell
					key={`${line}:${column.name}`}
					box={cellBox(block, line, place, tops)}
					unit={page.unit}
					name={`${where(line)}, ${elementLabel(column) || column.name}`}
					cell={sample ? column.name : `${line}:${column.name}`}
					scaled={scaled}
					className=""
					textStyle={textStyle}
				>
					{value}
				</Cell>
			);
		});
	});
}
